import type { Reimbursement, ReimbursementAct, ReimbursementStatus } from '@/data/seedMember'
import type { RawCareItem, RawOperation, RawOperationStatus } from '@/types/api'

/**
 * Maps a `refund` / `care` operation to the `Reimbursement` view-model.
 *
 * The wire format carries amounts as decimal strings and splits the coverage
 * between `ro_amount` (régime obligatoire — sécurité sociale) and `rc_amount`
 * (régime complémentaire — Smerra). The per-act breakdown lives in
 * `detail.data.care_items`; an operation may legitimately have no detail, in
 * which case only the billed amount is known.
 */

/** Decimal string → number. Absent or unparsable amounts count as zero: the
 *  back-end omits a share rather than sending 0 when it does not apply. */
function toAmount(value: string | null | undefined): number {
  const parsed = Number.parseFloat(value ?? '')
  return Number.isFinite(parsed) ? parsed : 0
}

/** Back-end refund statuses → statut affiché. The back-end compares the covered
 *  shares to the amount billed (`CreateCareRefund#refund_status`); any other
 *  status would not be a settled refund, so it reads as partial. */
function toStatus(status: RawOperationStatus | RawCareItem['status']): ReimbursementStatus {
  return status === 'refunded' ? 'total' : 'partial'
}

function mapCareItem(raw: RawCareItem): ReimbursementAct {
  return {
    label: raw.nature ?? '',
    cost: toAmount(raw.total_amount),
    socialSecurity: toAmount(raw.ro_amount),
    smerra: toAmount(raw.rc_amount),
    status: toStatus(raw.status),
  }
}

export function mapReimbursement(raw: RawOperation): Reimbursement {
  const data = raw.detail?.data ?? null
  const acts = (data?.care_items ?? []).map(mapCareItem)
  return {
    id: raw.id,
    // `event_time` is the start of the care; `performed_at` (the payment) is the
    // fallback when the decompte carried no care date.
    careDate: raw.event_time ?? raw.performed_at ?? raw.created_at,
    // A single-act decompte is named after its act; a multi-act one is labelled
    // by the view, which knows how to say « Multi-actes (n) ».
    label: acts.length === 1 ? acts[0].label : '',
    cost: toAmount(raw.total_amount),
    socialSecurity: toAmount(data?.ro_amount),
    smerra: toAmount(data?.rc_amount),
    status: toStatus(raw.status),
    acts,
  }
}
